<template>
  <div class="template-rich-text-input">
    <label v-if="label" class="block text-sm font-semibold text-gray-700 mb-1">
      {{ label }}
    </label>
    <div v-if="helpText" class="text-xs text-gray-500 mb-2">{{ helpText }}</div>

    <div class="rich-editor-wrap">
      <quill-editor
        :content="modelValue"
        content-type="html"
        :toolbar="toolbarConfig"
        :options="editorOptions"
        @update:content="onContentChange"
      />
    </div>

    <div class="mt-1 flex items-center justify-between gap-3 text-xs">
      <div class="text-gray-500">
        Allowed formatting: bold, italic, links, lists. Everything else is stripped on save.
      </div>
      <div v-if="maxLength" :class="overLimit ? 'text-red-600' : 'text-gray-500'">
        {{ characterCount }} / {{ maxLength }}
      </div>
    </div>
  </div>
</template>

<script>
import { QuillEditor } from '@vueup/vue-quill';
import '@vueup/vue-quill/dist/vue-quill.snow.css';

export default {
  name: 'TemplateRichTextInput',

  components: { QuillEditor },

  props: {
    modelValue: { type: String, default: '' },
    label: { type: String, default: '' },
    helpText: { type: String, default: '' },
    maxLength: { type: Number, default: 0 },
  },

  emits: ['update:modelValue'],

  data() {
    return {
      toolbarConfig: [
        ['bold', 'italic'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
        ['clean'],
      ],
      editorOptions: {
        placeholder: 'Type here…',
      },
    };
  },

  computed: {
    characterCount() {
      if (!this.modelValue) return 0;
      // Count visible characters by stripping tags + decoding common entities.
      // Intentionally does NOT parse HTML into DOM to avoid any XSS surface.
      return this.modelValue
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .length;
    },
    overLimit() {
      return this.maxLength > 0 && (this.modelValue?.length || 0) > this.maxLength;
    },
  },

  methods: {
    onContentChange(html) {
      // Quill emits '<p><br></p>' for empty content; normalize to empty string
      // so required/dirty checks behave intuitively.
      const normalized = html === '<p><br></p>' ? '' : html;
      this.$emit('update:modelValue', normalized);
    },
  },
};
</script>

<style scoped>
/* Quill's "snow" theme hard-codes Helvetica on .ql-container, so the body
   text renders in a font that clashes with the rest of the admin UI (Red Hat
   Text). Re-point the editable area at the app font and explicitly neutralize
   any inherited casing/variant treatment so the body always shows plain,
   sentence-case formatting — never an all-caps / display look. */
.rich-editor-wrap :deep(.ql-editor),
.rich-editor-wrap :deep(.ql-editor) * {
  font-family: 'Red Hat Text', system-ui, -apple-system, sans-serif;
  text-transform: none;
  font-variant: normal;
  font-variant-caps: normal;
  letter-spacing: normal;
}
.rich-editor-wrap :deep(.ql-editor) {
  min-height: 120px;
  font-size: 14px;
}
.rich-editor-wrap :deep(.ql-toolbar) {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
.rich-editor-wrap :deep(.ql-container) {
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
}
</style>
